import { PropsWithChildren, FC, useMemo, useCallback } from "react"
import {
  useTheme,
  BottomNavigationAction,
  Box,
  ButtonProps,
  BottomNavigationActionProps,
} from "@mui/material"
import { Link } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import { deepmerge } from "@mui/utils"
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded"
import { SearchIcon } from "components/icons"
import { LanguageButton } from "components/Language"
import { useAppUI } from "providers/ui"
import { getIcon } from "utils/icon"

interface ControlBottomNavigationActionProps
  extends BottomNavigationActionProps {
  component?: FC
  hidden?: boolean
}
interface ControlButtonProps extends ButtonProps {
  hidden?: boolean
}

interface NavigationControlProps extends PropsWithChildren {
  BackButtonProps?: ControlBottomNavigationActionProps
  SearchButtonProps?: ControlBottomNavigationActionProps
  HomeButtonProps?: ControlBottomNavigationActionProps
  LanguageButtonProps?: ControlButtonProps
}

export const NavigationControl: FC<NavigationControlProps> = (props) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { toggleSearch, mainMenus = [], goBack } = useAppUI()
  const {
    BackButtonProps: { hidden: hideBack, ...BackButtonProps } = {
      hidden: false,
    },
    SearchButtonProps: { hidden: hideSearch, ...SearchButtonProps } = {
      hidden: false,
    },
    LanguageButtonProps: { hidden: hideLanguage, ...LanguageButtonProps } = {
      hidden: false,
    },
  } = props

  const { styleOverrides = {} } = theme.components[`VuiNavigationControl`] || {}
  const {
    wrapper = {},
    navigationAction = {},
    languageButton = {},
  } = styleOverrides

  const backIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.backIcon) || (
        <KeyboardArrowLeftRoundedIcon />
      ),
    [theme]
  )

  const handleBack = useCallback(
    (e) => {
      // Prevent redirect from triggering the GTM.
      e.preventDefault()
      goBack()
    },
    [goBack]
  )

  const handleToggleSearch = useCallback(
    (e) => {
      // Prevent redirect from triggering the GTM.
      e.preventDefault()
      toggleSearch()
    },
    [toggleSearch]
  )

  return (
    <Box sx={wrapper}>
      {!hideBack && (
        <BottomNavigationAction
          sx={navigationAction}
          label={t("Back")}
          icon={backIcon}
          showLabel
          // Ensure this button applies shared styling with <a> menu elements for first/last-of-type selectors.
          component={Link}
          {...BackButtonProps}
          onClick={handleBack}
        />
      )}
      {mainMenus.map(({ icon, label, to, disabled = false }) => {
        return (
          <BottomNavigationAction
            key={`navigation-control-main-nav-${to}`}
            sx={navigationAction}
            icon={icon}
            label={t([label])}
            showLabel
            to={to}
            component={Link}
            disabled={disabled}
          />
        )
      })}
      {!hideSearch && (
        <BottomNavigationAction
          sx={navigationAction}
          label={t([`navigation.Search`, "Search"])}
          icon={<SearchIcon />}
          showLabel
          // Ensure this button applies shared styling with <a> menu elements for first/last-of-type selectors.
          component={Link}
          {...SearchButtonProps}
          onClick={handleToggleSearch}
        />
      )}
      {!hideLanguage && (
        <LanguageButton
          sx={deepmerge(navigationAction, languageButton)}
          {...LanguageButtonProps}
        />
      )}
    </Box>
  )
}
